import * as React from 'react';
import { Result, SessionProvider, Log, Events } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Permission, Res, TicketLocationDTO, OnlineReservationInListDTO, FlowAction, OnSelectLocationAction, CurrentActionDisplayProfileDTO, ActionButtonDefinitionDTO } from 'core/api';
import { Command } from 'pos/common';
import * as SalesStore from '../SalesStore';
import * as Images from 'pos/Images';
import * as CoreImages from 'core/img';
import { createMapsContent, createSaleCentersContent, SaleLocationStatus, ActionButtonDefinitionFactory, HQuickActionsPanel, PosScreen, Header, Sidebar, PhoneOnly, Footer, Filler, ActionButtonDefinition, Content, LargeDeviceOnly } from 'pos/ui';
import { DateUtils } from 'core/utils';
import { OrderingFacade, RestaurantFacade } from 'pos/facades';
import { userCanClaimTicket } from 'pos/modules/helpers/ticket-selection';
import { CustomerDisplay, UserActivityMonitor } from 'pos/services';
import { BarcodeEncoder } from 'pos/services';
import { Message, Modal, useOverlayExecutor } from 'core/ui-next/modal';
import { SidebarButton } from 'pos/ui/SidebarButton';
import { useBarcodeReaderRef, useInterval } from 'core/infrastructure-next';
import { initSale, initSaleOnLocation } from './initSale';
import { useSelectSaleLocationCallbacks } from './useSelectSaleLocationCallbacks';
import { ReservationContent } from './ReservationContent';
import getAvailableReservations from '../reservation/getAvailableReservations';
import LastSaleBanner from '../login/LastSaleBanner';
import { MoreActionsOverlay } from 'pos/ui/MoreActionsButton';
import { HStack, Text, VStack } from '@chakra-ui/react';
import { useIsOnTop } from 'core/ui-next/modal/ModalHooks';
import ReactDOM from 'react-dom';
import { useIsMounted } from 'core/ui-next';

const LocationStatus = SaleLocationStatus.create(RestaurantFacade.getRestaurantStatus);
const MapsContent = createMapsContent(LocationStatus);
const SaleCentersContent = createSaleCentersContent(LocationStatus);

const REFRESH_LOCATION_STATUS_INTERVAL_MS = 15_000;
const REFRESH_RESERVATIONS_INTERVAL_MS = 60_000;

type Props = { profile: CurrentActionDisplayProfileDTO, directActionButtons: ActionButtonDefinitionDTO[] } & Modal.Actions<void>;

declare global {
	interface CommandType {
		selectSaleLocation: () => Promise<Result>
	}
}

Command.register('selectSaleLocation', Preconditions.ensure(Permission.SaleLocation_Change), async (): Promise<Result> => {

	await LocationStatus.initialize();

	const saleCenters = LocationStatus.getAllSaleCenters();

	const maps = LocationStatus.getAllMaps();

	if (saleCenters.length + maps.length === 0) {
		await Message.show(Res.Msg.MGS_NOT_SALE_CENTER_OR_MAPS_AVAILABLE());
		return Result.error();
	}

	const profile = await RestaurantFacade.getDisplayProfile();
	const directActionButtons = await RestaurantFacade.getCustomActionButtons();

	return await Modal.show(SelectSaleLocationScreen, { profile, directActionButtons });
});


async function initSaleFromTicketBarcode(code: string): Promise<Result<FlowAction>> {

	const ticket = await UserActivityMonitor.registerWhile(async () => {
		const idResult = await BarcodeEncoder.tryDecodeId(BarcodeEncoder.EntityType.Ticket, code);
		if (!idResult.ok) {
			await Message.show(BarcodeEncoder.isQRCode(code)
				? Res.Msg.MSG_QR_IS_NOT_A_VALID_OPEN_TICKET_QR(code)
				: Res.Msg.MSG_BARCODE_IS_NOT_A_VALID_OPEN_TICKET_BARCODE(code));
			return null;
		}

		const tickets = await OrderingFacade.getOpenedTickets();
		const ticket = tickets.find(x => x.Id === idResult.value);
		if (!ticket) {
			await Message.show(Res.Msg.MSG_TICKET_NOT_FOUND());
			return null;
		}

		const canClaim = await userCanClaimTicket({
			Id: ticket.Id,
			CurrentUser: ticket.CurrentUser,
			OpenerUser: ticket.OpenerUser,
			BlockerPos: ticket.BlockerPos
		});
		if (!canClaim)
			return null;

		return ticket;
	});

	if (ticket == null)
		return Result.error();

	await SalesStore.loadTicketById(ticket.Id);
	return await initSale();
};


// Guardo la fecha en la he buscado la reserva y el modo de selección para cuando se vuelva a mostrar esta pantalla desde seleccionar usuario.
type SelectionMode = 'maps' | 'sale-centers' | 'reservation';
let lastReservationDate: Date = DateUtils.today();
let lastSelectionMode: Nullable<SelectionMode> = null;

function useAvailableReservations() {
	const [reservations, setReservations] = React.useState<OnlineReservationInListDTO[]>([]);
	const [isLoadingReservations, setIsLoadingReservations] = React.useState(false);
	const loadingReservationsRef = React.useRef(false);

	const refreshReservations = React.useCallback(async () => {
		if (loadingReservationsRef.current)
			return;

		try {
			loadingReservationsRef.current = true;
			setIsLoadingReservations(true);
			setReservations(await getAvailableReservations(lastReservationDate ?? DateUtils.today(), ''));
		} catch (err) {
			Log.warn(`No se ha podido cargar la lista de reservas online ${err}`);
		} finally {
			loadingReservationsRef.current = false;
			setIsLoadingReservations(false);
		}
	}, []);

	const loadReservationsForDate = React.useCallback(async (date: Date) => {
		lastReservationDate = date;
		await refreshReservations();
	}, [refreshReservations]);

	return {
		reservations,
		isLoadingReservations,
		refreshReservations,
		loadReservationsForDate
	};
}

function usePendingReservation(
	selectionMode: SelectionMode,
	setSelectedMode: React.Dispatch<React.SetStateAction<SelectionMode>>
) {
	const [pendingReservation, setPendingReservation] = React.useState<Nullable<OnlineReservationInListDTO>>(null);

	const selectLocationForReservation = React.useCallback((selectedReservation: OnlineReservationInListDTO) => {
		setPendingReservation(selectedReservation);
		setSelectedMode(selectionMode);
	}, [selectionMode, setSelectedMode]);

	const clearPendingReservation = React.useCallback(() => {
		setPendingReservation(null);
	}, []);

	return {
		pendingReservation,
		selectLocationForReservation,
		clearPendingReservation
	};
}

function SelectSaleLocationScreen(props: Props) {

	const isMounted = useIsMounted();
	const { close, profile, directActionButtons } = props;
	const { execute } = useOverlayExecutor();
	const screenRef = React.useRef<HTMLDivElement>(null);

	const [hasCashControlNotification, setHasCashControlNotification] = React.useState(false);
	const [hasTakeOutOrdersNotification, setHasTakeOutOrdersNotification] = React.useState(false);

	const [opacity, setOpacity] = React.useState(0);
	const [selection, setSelection] = React.useState(LocationStatus.getSelection());
	const { reservations, isLoadingReservations, refreshReservations, loadReservationsForDate } = useAvailableReservations();

	const [selectedMode, setSelectedMode] = React.useState<SelectionMode>(
		lastSelectionMode ?? selection.mode
	);
	const {
		pendingReservation,
		selectLocationForReservation,
		clearPendingReservation
	} = usePendingReservation(selection.mode, setSelectedMode);

	const isOnTop = useIsOnTop();
	const isReservationMode = selectedMode === 'reservation';

	const onReservationModeSelected = React.useCallback(async () => {
		setSelectedMode('reservation');
	}, []);

	const onMapsModeSelected = React.useCallback(async () => {
		setSelectedMode('maps');
		await LocationStatus.selectMap();
	}, []);

	const onSaleCenterModeSelected = React.useCallback(async () => {
		setSelectedMode('sale-centers');
		await LocationStatus.selectSaleCenter();
	}, []);

	const returnToReservationMode = React.useCallback(async () => {
		if (isReservationMode) {
			await refreshReservations();
			return;
		}

		setSelectedMode('reservation');
	}, [isReservationMode, refreshReservations]);

	const rememberReservationModeAfterSale = React.useCallback(() => {
		setSelectedMode('reservation');
	}, []);

	const refreshLocationStatus = React.useCallback(async () => {
		if (!isOnTop) return;

		try {
			await LocationStatus.initialize();
		} catch (err) {
			Log.warn(`No se ha podido recargar el estado de los planos de mesas ${err}`);
		}
	}, [isOnTop]);

	const refreshReservationsInReservationMode = React.useCallback(async () => {
		if (isOnTop && isReservationMode)
			await refreshReservations();

	}, [isOnTop, isReservationMode, refreshReservations]);

	const refreshCurrentModeData = React.useCallback(async () => {
		if (isReservationMode) {
			await refreshReservations();
		} else {
			await refreshLocationStatus();
		}
	}, [isReservationMode, refreshReservations, refreshLocationStatus]);

	const refreshCustomerDisplay = React.useCallback(async () => {
		if (!SessionProvider.getCurrent().ShowLastSaleInSelectLocationView) {
			await CustomerDisplay.clear();
			return;
		}

		if (SalesStore.isInitialized())
			await CustomerDisplay.displayTicketTotal(SalesStore.getTicket());
	}, []);

	const applyFlowAction = React.useCallback(
		async (action: FlowAction) => {

			if (!isMounted())
				return;

			if (action === FlowAction.ChangeUser) {
				close();
				return;
			}

			// Cuando volvemos nos hacemos visibles, puede ser que tuvieramos un goToDefaultLocation. Intento ser
			// lo más rápido posible, pero aun así se ve un pelín la de usuario
			ReactDOM.flushSync(() => setOpacity(1));

			clearPendingReservation();

			await refreshCurrentModeData();

			await refreshCustomerDisplay();
		},
		[close, clearPendingReservation, refreshCurrentModeData, refreshCustomerDisplay, isMounted]
	);

	const tryToGoToDefaultLocation = React.useCallback(async () => {
		const defaultLocation = SessionProvider.getDefaultLocation();
		const result = await initSaleOnLocation(defaultLocation.Location, null, defaultLocation.OnSelectLocationAction);

		if (result.ok) {
			await applyFlowAction(result.value);
			return;
		}

		close();
	}, [close, applyFlowAction]);

	React.useEffect(() => {
		LocationStatus.registerOnChange(newSelection => setSelection(newSelection));
		setOpacity(SessionProvider.getDefaultLocation().GoToDefaultLocation ? 0 : 1);
		return () => LocationStatus.unregisterOnChanged();
	}, []);

	const didAutoGoRef = React.useRef(false);
	React.useEffect(() => {
		if (!SessionProvider.getDefaultLocation().GoToDefaultLocation) return;
		if (didAutoGoRef.current) return;

		didAutoGoRef.current = true;
		void tryToGoToDefaultLocation();
	}, [tryToGoToDefaultLocation]);


	const onTicketBarcodeRead = React.useCallback(async (barcode: string) => {
		const result = await initSaleFromTicketBarcode(barcode);
		if (result.ok)
			await applyFlowAction(result.value);
	}, [applyFlowAction]);

	const onSessionTimeout = React.useCallback(async () => {
		if (isOnTop)
			close();
	}, [isOnTop, close]);

	useBarcodeReaderRef(onTicketBarcodeRead);

	useInterval(refreshLocationStatus, REFRESH_LOCATION_STATUS_INTERVAL_MS);
	useInterval(refreshReservationsInReservationMode, REFRESH_RESERVATIONS_INTERVAL_MS);


	Events.useEventOn('pos:cash-control:show-notification', async () => setHasCashControlNotification(true));
	Events.useEventOn('pos:cash-control:hide-notification', async () => setHasCashControlNotification(false));
	Events.useEventOn('pos:takeout-order:new-orders-count', async ({ count }) => setHasTakeOutOrdersNotification(count > 0));
	Events.useEventOn('session:timeout', onSessionTimeout);

	// Aseguro que cuando se cambia el modo seleccionado, se recalcula la información necesaria
	React.useEffect(() => {
		void refreshCurrentModeData();
		return () => { lastSelectionMode = selectedMode; };
	}, [selectedMode, refreshCurrentModeData]);


	const initSaleAtLocation = React.useCallback(async (location: TicketLocationDTO, reservation: Nullable<OnlineReservationInListDTO>) => {
		if (reservation !== null)
			rememberReservationModeAfterSale();

		await execute({
			action: async () => {
				const result = await initSaleOnLocation(location, reservation, OnSelectLocationAction.LoadOpenedOrCreateNewTicket);
				if (result.ok)
					await applyFlowAction(result.value);
			}
		});
	}, [execute, applyFlowAction, rememberReservationModeAfterSale]);

	const onLocationSelected = React.useCallback(async (location: TicketLocationDTO) => {
		await initSaleAtLocation(location, pendingReservation);

	}, [initSaleAtLocation, pendingReservation]);

	const getReservationLocation = React.useCallback((reservationLocation: string): Nullable<TicketLocationDTO> => {
		return LocationStatus.getAllSaleCenters().flatMap(s => s.SaleLocations).find(x => x.TicketLocation.SaleLocationName === reservationLocation)?.TicketLocation || null;
	}, []);

	const onReservationSelected = React.useCallback(async (selectedReservation: OnlineReservationInListDTO) => {

		const location = getReservationLocation(selectedReservation.Location);
		const result = await Command.execute('confirmAttendOnlineReservation', selectedReservation, location !== null);
		if (!result.ok)
			return;

		const requiresLocationSelection = location === null || result.value.attendAtOtherLocation;
		if (requiresLocationSelection) {
			selectLocationForReservation(selectedReservation);
			return;
		}

		await initSaleAtLocation(location, selectedReservation);

	}, [getReservationLocation, selectLocationForReservation, initSaleAtLocation]);

	const onCancelReservationAssign = React.useCallback(async () => {
		clearPendingReservation();
		await returnToReservationMode();
	}, [clearPendingReservation, returnToReservationMode]);

	const LocationContent = () => selection.mode === 'maps'
		? <MapsContent onSaleLocationSelected={onLocationSelected} selected={selection.selectedMap} />
		: <SaleCentersContent onSaleLocationSelected={onLocationSelected} selected={selection.selectedSaleCenter} />;

	const callbacks = useSelectSaleLocationCallbacks({ hasCashControlNotification: () => hasCashControlNotification, hasTakeOutOrdersNotification: () => hasTakeOutOrdersNotification, applyFlowAction });

	const rows = profile.SelectSaleLocationViewButtonRows;
	const columns = profile.SelectSaleLocationViewButtonColumns;
	const hasMaps = LocationStatus.getAllMaps().length > 0;
	const otherButtonsDefs = ActionButtonDefinitionFactory.createForSelectSaleLocationScreen(directActionButtons, props.profile, callbacks);

	const closeSessionFooterBtn: ActionButtonDefinition = { image: CoreImages.Back, title: Res.Web.CLOSE_SESSION_ACTION_BTN(), type: 'secondary', action: async () => close() };
	const attendReservationFooterBtn: Nullable<ActionButtonDefinition> = SessionProvider.isOnlineReservationModuleEnabled()
		? { title: Res.Web.SELECT_SALE_LOCATION_ONLINE_RESERVATION_BTN(), image: Images.OnlineReservation, action: async () => onReservationModeSelected() }
		: null;
	const SaleCenterFooterBtn: ActionButtonDefinition = { title: Res.Web.SELECT_SALE_LOCATION_SALE_CENTER_BTN(), image: Images.SaleCenter, action: onSaleCenterModeSelected };
	const SaleMapFooterBtn: Nullable<ActionButtonDefinition> = hasMaps ? { title: Res.Web.SELECT_SALE_LOCATION_MAPS_BTN(), image: Images.Map, action: onMapsModeSelected } : null;

	return (
		<PosScreen screenId='SELECT_SALE_LOCATION' ref={screenRef} size={'full-screen'} opacity={opacity} showOnlyWhileOnTop>

			<Header title={Res.Web.SELECT_SALE_LOCATION()} image={Images.AgoraLogo} onClose={() => { }} onUserClick={close} />

			<Sidebar>
				{hasMaps ? <SidebarButton text={Res.Web.SELECT_SALE_LOCATION_MAPS_BTN()} icon={Images.Map} action={onMapsModeSelected} selectedMode={selectedMode === 'maps'} /> : null}
				<SidebarButton text={Res.Web.SELECT_SALE_LOCATION_SALE_CENTER_BTN()} icon={Images.SaleCenter} action={onSaleCenterModeSelected} selectedMode={selectedMode === 'sale-centers'} />
				{SessionProvider.isOnlineReservationModuleEnabled() && <SidebarButton text={Res.Web.SELECT_SALE_LOCATION_ONLINE_RESERVATION_BTN()} icon={Images.OnlineReservation} action={onReservationModeSelected} selectedMode={selectedMode === 'reservation'} />}

				<MoreActionsOverlay
					buttons={otherButtonsDefs}
					columns={columns}
					rows={rows}
					renderTrigger={({ toggle }) =>
						<SidebarButton text={Res.Web.OTHER_BTN()} icon={Images.MoreOptions} action={toggle} />
					} />

				<Filler />
				<SidebarButton text={Res.Web.CHANGE_USER_BTN()} icon={Images.ChangeUser} action={close} />
			</Sidebar>

			<Content>
				<VStack w={'full'} h={'full'} gap={'0'} overflow={'auto'}>
					{selectedMode !== 'reservation' && SessionProvider.showLastSaleInfoInSelectSaleLocationView() && <LastSaleBanner lastSale={SalesStore.getLastSale()} />}
					{pendingReservation !== null &&
						<>
							<LargeDeviceOnly>
								<HStack paddingX={'6'} paddingTop={'6'} paddingBottom={selectedMode === 'sale-centers' ? '0' : '6'} width={'full'} justifyContent={'space-between'} position={'sticky'} top={0} bgColor={{ base: 'white', _dark: 'neutral.900' }} zIndex={1}>
									<Text fontWeight={'semibold'} fontSize={'md'}>{Res.Web.CONFIRM_ATTEND_ONLINE_RESERVATION_CUSTOMER(pendingReservation.Customer)}</Text>
									<Text
										fontWeight={'semibold'}
										fontSize={'xs'}
										borderWidth={'1px'}
										borderColor={'info'}
										borderRadius={'md'}
										color={'info'}
										cursor={'pointer'}
										paddingY={'1'}
										paddingX={'2'}
										as={'button'}
										onClick={onCancelReservationAssign}
									>
										{Res.Web.CANCEL_ONLINE_RESERVATION_NEW_ASSIGN_BTN()}
									</Text>
								</HStack>
							</LargeDeviceOnly>
							<PhoneOnly>
								<HStack paddingX={'2'} paddingTop={'4'} paddingBottom={'2'} width={'full'} justifyContent={'space-between'} position={'sticky'} top={0} bgColor={{ base: 'white', _dark: 'neutral.900' }} zIndex={1}>
									<Text flex={1} fontWeight={'semibold'} fontSize={'sm'} truncate>{Res.Web.CONFIRM_ATTEND_ONLINE_RESERVATION_CUSTOMER_SHORT(pendingReservation.Customer)}</Text>
									<Text width={'auto'}
										fontWeight={'normal'}
										fontSize={'xs'}
										borderWidth={'1px'}
										borderColor={'info'}
										borderRadius={'md'}
										color={'info'}
										cursor={'pointer'}
										paddingY={'0.5'}
										paddingX={'2'}
										textAlign={'center'}
										as={'button'}
										truncate
										onClick={onCancelReservationAssign}
									>
										{Res.Web.CANCEL_ONLINE_RESERVATION_NEW_ASSIGN_BTN_SHORT()}
									</Text>
								</HStack>
							</PhoneOnly>
						</>
					}
					{selectedMode === 'reservation' ? <ReservationContent loading={isLoadingReservations} reservations={reservations} onReservationSelected={onReservationSelected} onDateChanged={loadReservationsForDate} date={lastReservationDate} /> : <LocationContent />}
				</VStack>
			</Content>

			<Footer>
				<PhoneOnly>
					<HQuickActionsPanel
						surface='footer'
						buttons={[closeSessionFooterBtn, null, attendReservationFooterBtn, SaleCenterFooterBtn, SaleMapFooterBtn]}
						extraButtons={{ rows, columns, buttons: otherButtonsDefs }} />
				</PhoneOnly>
			</Footer>

		</PosScreen>
	);
}
